'use client';
import { useState } from 'react';
import StarIcon from '@/components/ui/icons/star-icon';
import ReviewModal from '../review-modal';
import { useSession } from 'next-auth/react';
import NoRatings from './no-rating';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useLocale } from 'next-intl';

const ProductRatings = ({ reviews, t, entity_id }) => {
  const { data: session } = useSession();

  const { push } = useRouter();
  const locale = useLocale();
  const pathName = usePathname();

  const redirectUrl = `/${locale}${pathName}`;

  const isUserExist = session?.user;
  const [showModal, setShowModal] = useState(false);
  const totalReviews =
    reviews?.filter((e) => e?.ratings?.length !== 0)?.length ?? 0;

  const ratingValues = reviews
    ?.map((r) => r?.ratings?.[0]?.value ?? 0)
    ?.filter((v) => v > 0);

  const avgRating =
    ratingValues?.length > 0
      ? (
          ratingValues?.reduce((a, b) => a + b, 0) / ratingValues?.length
        )?.toFixed(1)
      : 0;

  const counts = [5, 4, 3, 2, 1].map(
    (star) => ratingValues?.filter((v) => v === star)?.length
  );

  const percentages = counts?.map((count) =>
    totalReviews ? Math?.round((count / totalReviews) * 100) : 0
  );

  const openRatingModal = () => {
    if (!isUserExist) {
      sessionStorage.setItem('loginRedirect', redirectUrl);
      push('/login');
      return;
    }
    setShowModal(true);
  };

  return (
    <>
      {!!reviews?.length ? (
        <div className='max-w-3xl'>
          <div className='mb-6 flex flex-col gap-4 md:flex-row md:items-center'>
            <div className='flex items-center gap-4'>
              <span className='text-5xl font-bold'>{avgRating}</span>
              <div className='flex flex-col'>
                <div className='flex'>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      size={20}
                      className={`${
                        star <= Math.round(avgRating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className='mt-1 text-sm text-gray-500'>
                  {t('Based on')} {totalReviews}{' '}
                  {totalReviews === 1 ? t('review') : t('reviews')}
                </span>
              </div>
            </div>

            <div className='flex-1 space-y-1.5'>
              {[5, 4, 3, 2, 1].map((star, idx) => (
                <div key={star} className='flex items-center gap-3'>
                  <span className='w-4 text-sm'>{star}</span>
                  <div className='h-2.5 flex-1 overflow-hidden rounded-full bg-gray-200'>
                    <div
                      className='h-full rounded-full bg-yellow-400 transition-all duration-500'
                      style={{
                        width: `${percentages[idx]}%`,
                      }}
                    ></div>
                  </div>
                  <span className='w-12 text-right text-sm text-gray-500'>
                    {percentages[idx]}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className='mt-4'>
            <button
              onClick={openRatingModal}
              className='rounded-lg bg-[#1e2b55] px-6 py-2 font-medium text-white transition-colors hover:bg-[#283a6e]'
            >
              {t('Write a Review')}
            </button>
          </div>
        </div>
      ) : (
        <NoRatings t={t} openRatingModal={openRatingModal} />
      )}

      {showModal && (
        <ReviewModal
          open={showModal}
          onClose={() => setShowModal(false)}
          entity_id={entity_id}
          t={t}
        />
      )}
    </>
  );
};

export default ProductRatings;
